import React, { useState } from 'react';
import { Send, Phone, MessageSquare, Mail, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1635367216109-aa3353c0c22e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxoZWFsdGglMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NzA3NjE1NjZ8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/80 to-cyan-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Mail className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl">We're here to help and answer any questions you have</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Helpline Card */}
            <Card className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <Phone className="h-12 w-12 mb-4" />
                <h3 className="text-xl font-bold mb-2">Alcohol Drug Helpline</h3>
                <p className="mb-4 text-sm">Free, confidential support 24/7</p>
                <div className="space-y-2">
                  <p className="text-lg font-semibold">0800 787 797</p>
                  <p className="text-sm">Free call anytime</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <MessageSquare className="h-12 w-12 mb-4" />
                <h3 className="text-xl font-bold mb-2">Text Support</h3>
                <p className="mb-4 text-sm">Quick and confidential</p>
                <div className="space-y-2">
                  <p className="text-lg font-semibold">8681</p>
                  <p className="text-sm">Free text anytime</p>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <MapPin className="h-12 w-12 mb-4 text-teal-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Location</h3>
                <p className="text-gray-700">Serving all of New Zealand</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold text-gray-700">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold text-gray-700">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-base font-semibold text-gray-700">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="What is this about?"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold text-gray-700">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-2"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                  
                  {submitted && (
                    <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg">
                      <p className="text-green-800 font-semibold">Message Sent!</p>
                      <p className="text-green-700 text-sm">Thank you for contacting us. We'll get back to you soon.</p>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Card className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Before You Contact Us</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 mt-1">•</span>
                    <span>For urgent help with alcohol issues, please call the Alcohol Drug Helpline: 0800 787 797</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 mt-1">•</span>
                    <span>We aim to respond to all inquiries within 2-3 business days</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 mt-1">•</span>
                    <span>For media inquiries, please include "MEDIA" in your subject line</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 mt-1">•</span>
                    <span>Check our website for answers to common questions before contacting us</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;